import React, { useState, useEffect, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import {
  FunnelSimple,
  Eye,
  FloppyDisk,
  Buildings,
  House,
} from '@phosphor-icons/react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import {
  LogicalOperator,
  UnitFilterCondition,
  UnitFilterCriteria,
  UnitFilterGroup,
  FilterableFieldConfig,
  UnitFilterPreviewResponse,
} from 'shared/types/cluster';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { UnitFilterGroupEditor } from './UnitFilterGroupEditor';

interface UnitFilterBuilderProps {
  clusterId: number;
  initialCriteria?: UnitFilterCriteria | null;
  onSaved?: () => void;
}

export const UnitFilterBuilder: React.FC<UnitFilterBuilderProps> = ({
  clusterId,
  initialCriteria,
  onSaved,
}) => {
  const { session } = useSession();
  const [fieldConfigs, setFieldConfigs] = useState<FilterableFieldConfig[]>([]);
  const [isLoadingFields, setIsLoadingFields] = useState(true);
  const [rootGroup, setRootGroup] = useState<UnitFilterGroup>(
    initialCriteria?.root || {
      id: crypto.randomUUID(),
      logicalOperator: LogicalOperator.AND,
      conditions: [],
    },
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [preview, setPreview] = useState<UnitFilterPreviewResponse | null>(null);

  // Fetch field configs from backend
  useEffect(() => {
    const fetchFields = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/clusters/unit-filter/fields`,
          { method: 'GET', headers: { 'Content-Type': 'application/json' } },
          session,
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch field configs: ${response.statusText}`);
        }
        const data = await response.json();
        setFieldConfigs(data);
      } catch (error) {
        console.error('Error fetching filter field configs:', error);
        toast.error('Kon filtervelden niet laden');
      } finally {
        setIsLoadingFields(false);
      }
    };
    fetchFields();
  }, [session]);

  // Reset root group when initialCriteria changes
  useEffect(() => {
    if (initialCriteria?.root) {
      setRootGroup(initialCriteria.root);
    }
  }, [initialCriteria]);

  const buildCriteria = useCallback((): UnitFilterCriteria => {
    return {
      version: '1.0',
      root: stripIds(rootGroup),
    };
  }, [rootGroup]);

  const handlePreview = async () => {
    try {
      setIsPreviewing(true);
      const criteria = buildCriteria();
      const response = await fetchWithAuth(
        `${API_URL}/clusters/unit-filter/preview`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ unitFilterCriteria: criteria, limit: 50 }),
        },
        session,
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Preview mislukt');
      }

      const data: UnitFilterPreviewResponse = await response.json();
      setPreview(data);
    } catch (error) {
      console.error('Error previewing filter:', error);
      toast.error(error instanceof Error ? error.message : 'Kon preview niet laden');
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const criteria = buildCriteria();

      // Validate first
      const validateResponse = await fetchWithAuth(
        `${API_URL}/clusters/unit-filter/validate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ unitFilterCriteria: criteria }),
        },
        session,
      );

      if (validateResponse.ok) {
        const validation = await validateResponse.json();
        if (!validation.isValid) {
          toast.error(`Ongeldige filter: ${validation.errors?.join(', ') || 'Onbekende fout'}`);
          return;
        }
      }

      // Save to cluster
      const response = await fetchWithAuth(
        `${API_URL}/clusters/${clusterId}/unit-filter-criteria`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ unitFilterCriteria: criteria }),
        },
        session,
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Opslaan mislukt');
      }

      toast.success('Filtercriteria opgeslagen');
      onSaved?.();
    } catch (error) {
      console.error('Error saving filter criteria:', error);
      toast.error(error instanceof Error ? error.message : 'Kon filter niet opslaan');
    } finally {
      setIsSaving(false);
    }
  };

  const conditionCount = countConditions(rootGroup);

  if (isLoadingFields) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Spinner className="size-6 text-moss-primary" />
          <span className="ml-2 text-sm text-muted-foreground">
            Filtervelden laden...
          </span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Builder Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FunnelSimple className="size-5 text-moss-primary" weight="duotone" />
                Filtercriteria
              </CardTitle>
              <CardDescription>
                Definieer voorwaarden om eenheden automatisch aan deze cluster toe te wijzen
              </CardDescription>
            </div>
            <Badge variant="secondary" className="text-xs">
              {conditionCount} voorwaarde{conditionCount !== 1 ? 'n' : ''}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <UnitFilterGroupEditor
            group={rootGroup}
            fieldConfigs={fieldConfigs}
            onChange={setRootGroup}
          />

          <Separator />

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreview}
              disabled={isPreviewing || conditionCount === 0}
            >
              {isPreviewing ? (
                <Spinner className="size-4" />
              ) : (
                <Eye className="size-4" />
              )}
              Voorbeeld
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={isSaving}
              className="bg-moss-primary hover:bg-moss-primary/90 text-white"
            >
              {isSaving ? (
                <Spinner className="size-4" />
              ) : (
                <FloppyDisk className="size-4" />
              )}
              Filter opslaan
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Results */}
      {preview && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="size-4 text-moss-primary" weight="duotone" />
              Voorbeeld resultaten
            </CardTitle>
            <CardDescription>
              {preview.totalCount} eenhe{preview.totalCount === 1 ? 'id' : 'den'} in{' '}
              {preview.buildingSummary.length} gebouw{preview.buildingSummary.length !== 1 ? 'en' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {preview.buildingSummary.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {preview.buildingSummary.map((bs) => (
                  <div key={bs.buildingId} className="flex items-center gap-2">
                    <Buildings className="size-4 text-muted-foreground shrink-0" weight="duotone" />
                    <span className="text-sm text-foreground truncate flex-1">
                      {bs.buildingAddress || bs.buildingId}
                    </span>
                    <Badge variant="secondary" className="text-xs shrink-0">
                      {bs.matchingUnitCount}/{bs.totalUnitCount}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-sm text-muted-foreground">
                <House className="size-8 mx-auto mb-2 text-muted-foreground/50" weight="duotone" />
                Geen eenheden gevonden met deze criteria
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/** Recursively count leaf conditions in a group */
function countConditions(group: UnitFilterGroup): number {
  let count = 0;
  for (const item of group.conditions) {
    if ('logicalOperator' in item && 'conditions' in item) {
      count += countConditions(item as UnitFilterGroup);
    } else {
      count += 1;
    }
  }
  return count;
}

/** Strip frontend-only `id` fields before sending to backend */
function stripIds(group: UnitFilterGroup): UnitFilterGroup {
  return {
    logicalOperator: group.logicalOperator,
    conditions: group.conditions.map((item) => {
      if ('logicalOperator' in item && 'conditions' in item) {
        return stripIds(item as UnitFilterGroup);
      }
      const condition = item as UnitFilterCondition;
      const result: UnitFilterCondition = {
        field: condition.field,
        operator: condition.operator,
        value: condition.value,
      };
      if (condition.valueEnd !== undefined) {
        result.valueEnd = condition.valueEnd;
      }
      return result;
    }),
  };
}

export default UnitFilterBuilder;
